"use client";

import React, { ReactNode } from "react";
import Link from "next/link";

type ButtonProps = {
  children: ReactNode;
  variant?: "primary" | "secondary" | "outline";
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  ariaLabel?: string;
  link?: string;
  title?: string;
  type?: "button" | "submit" | "reset";
};

export default function CustomButton({
  children,
  type,
  variant = "primary",
  leftIcon,
  rightIcon,
  onClick,
  className = "",
  link,
  ariaLabel,
  title,
  disabled = false,
  fullWidth = false,
}: ButtonProps) {
  const baseClasses = `
    inline-flex items-center justify-center
    rounded-md font-semibold px-6 py-3
    transition duration-200
    disabled:opacity-50 disabled:cursor-not-allowed
    ${fullWidth ? "w-full" : "inline-flex"}
  `;

  const variantClasses = {
    primary:
      "bg-[#F0C869] text-black hover:bg-yellow-400 active:bg-yellow-600 mb-4 shadow-[0_4px_10px_0_rgba(240,200,105,0.5)] hover:shadow-[0_6px_15px_0_rgba(240,200,105,0.7)]",
    secondary: "bg-gray-700 text-white hover:bg-gray-600 active:bg-gray-800",
    outline:
      "border border-[#F0C869] text-white hover:bg-[#F0C869] hover:text-black mb-4 shadow-[0_4px_10px_0_rgba(240,200,105,0.5)] hover:shadow-[0_6px_15px_0_rgba(240,200,105,0.7)]",
  }[variant];

  // If link is provided → render Next.js <Link>
  if (link) {
    return (
      <Link href={link} className={`${baseClasses} ${variantClasses} ${className}`}>
        {leftIcon && <span className="mr-2 flex items-center">{leftIcon}</span>}
        {children}
        {rightIcon && <span className="ml-2 flex items-center">{rightIcon}</span>}
      </Link>
    );
  }

  // Otherwise render a <button>
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      type={type || "button"}
      aria-label={ariaLabel}
      title={title}
      className={`${baseClasses} ${variantClasses} ${className} cursor-pointer`}
    >
      {leftIcon && <span className="mr-2 flex items-center">{leftIcon}</span>}
      {children}
      {rightIcon && <span className="ml-2 flex items-center">{rightIcon}</span>}
    </button>
  );
}
